import { STEP1_LIFE_STORY_PROMPTS } from '../config/step1LifeStoryPrompts';
import { Step1WizardAnswerEntry } from '../types/step1Writing';
import { formatAloudDurationLabel } from './readTimeEstimate';

/** Suggest trimming when draft exceeds this word count (~20+ min at slowest pace) */
export const LONG_DRAFT_CHECKLIST_WORD_THRESHOLD = 3500;

export interface DraftChecklistItem {
  id: string;
  severity: 'info' | 'suggestion' | string;
  feedback: string;
  recommendation: string;
  promptId?: string;
}

function blocksFromAnswers(wizardAnswers: Record<string, Step1WizardAnswerEntry>): string[] {
  const blocks: string[] = [];
  for (const prompt of STEP1_LIFE_STORY_PROMPTS) {
    const entry = wizardAnswers[prompt.id];
    if (!entry || entry.status === 'skipped' || entry.status === 'na' || entry.status === 'covered_elsewhere') {
      continue;
    }
    const text = (entry.examples || []).map((e) => e.trim()).filter(Boolean).join('\n\n');
    if (text) blocks.push(text);
  }
  return blocks;
}

/** Verbatim assembly — user words only, paragraphs separated by blank lines */
export function assembleVerbatimDraft(wizardAnswers: Record<string, Step1WizardAnswerEntry>): string {
  return blocksFromAnswers(wizardAnswers).join('\n\n');
}

export function blocksForFlowWeave(wizardAnswers: Record<string, Step1WizardAnswerEntry>): string[] {
  return blocksFromAnswers(wizardAnswers);
}

/** Join walkthrough answer blocks with blank lines */
export function localFlowWeave(blocks: string[]): string {
  return blocks.map((b) => b.trim()).filter(Boolean).join('\n\n');
}

/** Non-AI keyword checklist for common First Step topics */
export function localDraftChecklist(
  contentText: string,
  _wizardAnswers?: Record<string, Step1WizardAnswerEntry>
): DraftChecklistItem[] {
  const issues: DraftChecklistItem[] = [];
  const text = contentText.trim();
  const lower = text.toLowerCase();

  if (!text) {
    return issues;
  }

  if (!lower.includes('sex addict') && !lower.includes('sexual addict')) {
    issues.push({
      id: 'introduction',
      severity: 'suggestion',
      feedback: 'Many First Steps start with your first name and saying you are a sex addict.',
      recommendation: 'Add that at the start if it fits how you want to share.',
      promptId: 'intro_name',
    });
  }

  if (!lower.includes('powerless') && !lower.includes('could not stop') && !lower.includes("couldn't stop")) {
    issues.push({
      id: 'powerlessness',
      severity: 'suggestion',
      feedback: 'Include a time you tried to stop or control your behavior and couldn\'t.',
      recommendation: 'One clear example is enough — that\'s the heart of Step One.',
      promptId: 'powerlessness_moment',
    });
  }

  if (!lower.includes('unmanageable') && !lower.includes('consequence') && !lower.includes('damage')) {
    issues.push({
      id: 'unmanageability',
      severity: 'suggestion',
      feedback: 'Say how the addiction hurt parts of your life — relationships, work, money, health, or self-respect.',
      recommendation: 'One or two examples are enough.',
      promptId: 'unmanageability_overview',
    });
  }

  const wordCount = text.split(/\s+/).filter(Boolean).length;
  if (wordCount < 200) {
    issues.push({
      id: 'length',
      severity: 'info',
      feedback: `Your draft is short (${wordCount.toLocaleString()} words) — that's OK while you're getting started.`,
      recommendation: 'Add more detail wherever you feel ready.',
    });
  }

  if (wordCount > LONG_DRAFT_CHECKLIST_WORD_THRESHOLD) {
    const aloud = formatAloudDurationLabel(wordCount);
    issues.push({
      id: 'length_long',
      severity: 'info',
      feedback: `Your draft is about ${wordCount.toLocaleString()} words — ${aloud}.`,
      recommendation: 'You can shorten later if you want; nothing is changed unless you edit yourself.',
    });
  }

  return issues.slice(0, 4);
}

/** Stable fingerprint of walkthrough answers — used to detect when re-assembly is needed. */
export function fingerprintWizardAnswers(
  wizardAnswers: Record<string, Step1WizardAnswerEntry>
): string {
  const parts = STEP1_LIFE_STORY_PROMPTS.map((prompt) => {
    const entry = wizardAnswers[prompt.id];
    const examples = (entry?.examples || []).map((e) => e.trim());
    const status = entry?.status || (examples.some(Boolean) ? 'answered' : 'empty');
    return `${prompt.id}:${status}:${examples.join('\x1e')}`;
  });
  return parts.join('\x1f');
}
